import React, { useEffect, useRef, useState } from "react";
import SectionHeading from "./SectionHeading";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import {
  FaPlay,
  FaArrowLeft,
  FaArrowRight,
  FaVideo,
} from "react-icons/fa";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import axiosInstance from "../../../utils/axios";

const getVideoThumbnail = (videoUrl, second = 1) => {
  return videoUrl
    .replace("/upload/", `/upload/so_${second}/`)
    .replace(".mp4", ".jpg");
};

const SkeletonCard = () => (
  <div className="relative rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse">
    {/* Delete button placeholder */}
    <div className="absolute top-4 right-4 w-8 h-8 bg-gray-500 rounded-full z-20" />

    {/* Sequence badge placeholder */}
    <div className="absolute top-4 left-4 w-8 h-8 bg-gray-500 rounded-full z-20" />

    {/* Thumbnail placeholder */}
    <div className="relative pb-[140%] px-[20%] h-0 bg-gray-200">
      <div className="absolute inset-0 bg-gray-300" />

      {/* Play button placeholder */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-400/40 border border-gray-500/50 rounded-full flex items-center justify-center shadow-md">
          <FaPlay className="text-gray-200 text-lg md:text-xl ml-0.5" />
        </div>
      </div>
    </div>

    {/* Gradient overlay placeholder */}
    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-400 to-transparent" />

    {/* Title placeholder */}
    <div className="absolute bottom-4 left-4 right-4 h-4 bg-gray-500 rounded w-3/4 mx-auto" />
  </div>
);

const carouselConfig = {
  autoplayDelay: 5000,
  spaceBetween: 30,
  breakpoints: {
    0: { slidesPerView: 1, slidesPerGroup: 1 },
    640: { slidesPerView: 2, slidesPerGroup: 1 },
    768: { slidesPerView: 3, slidesPerGroup: 1 },
    1024: { slidesPerView: 4, slidesPerGroup: 1 },
  },
};

const VideoCard = ({ video, onPlay, playingVideo }) => {
  return (
    <div className="relative pb-[140%] px-[20%] h-0 overflow-hidden rounded-lg shadow-lg group cursor-pointer">
      {playingVideo === video?._id ? (
        <video
          src={video.videoUrl}
          className="absolute top-0 left-0 w-full h-full object-cover"
          controls
          autoPlay
        />
      ) : (
        <>
          <div
            className="absolute top-0 left-0 w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
            style={{
              backgroundImage: `url(${getVideoThumbnail(video.videoUrl)})`,
            }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-all duration-300"></div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlay(video);
            }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="w-14 h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-all duration-300">
              <FaPlay className="text-white text-lg md:text-xl ml-0.5" />
            </div>
          </button>
          {video.title && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
              <h3 className="text-white font-semibold text-sm md:text-base truncate">
                {video.title}
              </h3>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AFSATestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const swiperRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const fetchTestimonials = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/testimonials`);
      setTestimonials(res.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleVideoPlay = (video) => {
    setPlayingVideo(video?._id);
  };

  const handleMouseEnter = () => swiperRef.current?.autoplay?.stop?.();
  const handleMouseLeave = () => swiperRef.current?.autoplay?.start?.();

  const goToPrev = () => swiperRef.current?.slidePrev();
  const goToNext = () => swiperRef.current?.slideNext();

  return (
    <div
      className="general-p-m flex flex-col justify-center mx-auto overflow-hidden"
      data-aos="fade-up"
      data-aos-anchor-placement="top-bottom"
    >
      {/* Heading */}
      <SectionHeading
        subtitle={"Testimonials"}
        title={"Voice Of Community"}
        data-aos="fade-up"
        data-aos-delay="100"
      />

      <div className="w-full relative mt-6 sm:mt-8 md:mt-10">
        {/* Loading State */}
        {loading && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}


        {/* Empty State */}
        {!loading && !error && testimonials.length === 0 && (
          <div
            className="max-w-lg mx-auto bg-gray-50 border border-gray-200 rounded-xl p-6 text-center shadow-md"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <FaVideo className="text-gray-400 text-3xl mx-auto mb-2" />
            <h3 className="text-gray-700 font-semibold mb-2">
              No testimonials yet
            </h3>
            <p className="text-gray-500 text-sm">
              Check back later for more community voices.
            </p>
          </div>
        )}

        {/* Success State */}
        {!loading && !error && testimonials.length > 0 && (
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <Swiper
              modules={[Autoplay, Navigation]}
              spaceBetween={carouselConfig.spaceBetween}
              slidesPerView={1}
              breakpoints={carouselConfig.breakpoints}
              autoplay={{
                delay: carouselConfig.autoplayDelay,
                disableOnInteraction: false,
              }}
              loop={false}
              allowTouchMove={true}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
              centeredSlides={true}
              onSlideChange={(swiper) => {
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
            >
              {testimonials.map((video, idx) => (
                <SwiperSlide key={video._id}>
                  <VideoCard
                    video={video}
                    playingVideo={playingVideo}
                    onPlay={handleVideoPlay}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation Buttons */}
            <div
              className="flex justify-center items-center space-x-6 mt-4"
              data-aos="fade-up"
              data-aos-delay="500"
            >
              <button
                onClick={goToPrev}
                disabled={isBeginning}
                className={`w-12 h-12 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isBeginning
                    ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-50"
                    : "bg-white border-gray-200 shadow-lg hover:shadow-xl hover:border-[#FCC101]"
                }`}
              >
                <FaArrowLeft className="text-gray-600" />
              </button>

              <button
                onClick={goToNext}
                disabled={isEnd}
                className={`w-12 h-12 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isEnd
                    ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-50"
                    : "bg-white border-gray-200 shadow-lg hover:shadow-xl hover:border-[#FCC101]"
                }`}
              >
                <FaArrowRight className="text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AFSATestimonials;
